// crewbit-admin/src/components/money/MoneyInvoiceAuthoringPanel.tsx
//
// Spec: §InvoiceAuthoring.7 (admin parallel). Top-level slide-over composing
// the single-form authoring surface on top of useInvoiceDraft. Owns
// AuthoringDraftState. Routes terminal actions (Send now / Save draft /
// Send & record cash) through invoiceMutations wrappers (each emits
// MONEY_MUTATION_EVENT per Session 125 11-emit completeness fix).
//
// Idempotency key generated INSIDE useInvoiceDraft via useState initializer
// (line 56) — ONE per panel-mount.
//
// is_sales_receipt flag flips automatically on the cash path when clientId is
// null (walk-in cash) per Decision §InvoiceAuthoring.D8. Admin uses the
// Session 127 useInvoiceDraft.save(override) signature to pass the flip
// directly into the immediate save (avoiding stateRef-staleness); mobile
// parallel still relies on stateRef and patches via setState — to be cross-
// paired via §InvoiceAuthoring.MobileSaveOverrideAndIdReturn.
//
// Resume hydration: when `resumeInvoiceId` is provided (§3e Edit-from-detail
// flow), fetches the existing invoice + line_items, populates state, and
// passes `initialDraftInvoiceId` to useInvoiceDraft so first auto-save calls
// update_invoice instead of minting a duplicate draft.

import { useCallback, useEffect, useState } from 'react';
import { SlidePanel } from '../common/SlidePanel';
import { Toast } from '../common/Toast';
import { AuthoringClientSection } from './AuthoringClientSection';
import { AuthoringLineItemsSection } from './AuthoringLineItemsSection';
import { AuthoringReviewSection } from './AuthoringReviewSection';
import { AuthoringActionFooter } from './AuthoringActionFooter';
import { StartFromShapeEntryButton } from './StartFromShapeEntryButton';
import { StartFromShapeSheet } from './StartFromShapeSheet';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import type { InvoiceShapeApplyBlob } from '../../types/invoiceShapes';
import {
  useInvoiceDraft,
  resolveTaxRatePercent,
  type AuthoringDraftState,
} from '../../hooks/useInvoiceDraft';
import {
  linesFromCents,
  subtotalDollars,
  taxDollars,
  type AuthoringLine,
  type RpcLineItem,
} from '../../lib/invoiceLineItems';
import {
  deleteDraftInvoice,
  markPaidManually,
  markSent,
} from '../../lib/invoiceMutations';
import { lookupErrorCopy, type MoneyErrorCode } from '../../lib/errorMap';
import { applyShapeBlobToDraft } from '../../lib/invoiceShapes';
import { addMoney } from '../../lib/money';
import { supabase } from '../../lib/supabase';
import { palette, spacing } from '../../styles/tokens';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Existing draft invoice id for resume-from-detail-panel Edit (§3e).
   * Undefined → new-invoice flow (mints fresh draft on first auto-save).
   */
  resumeInvoiceId?: string;
}

type Action = 'send' | 'draft' | 'cash' | null;
type ToastState = { msg: string; type: 'success' | 'error' | 'info' } | null;

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function makeBlankLine(): AuthoringLine {
  return {
    id: `line-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    description: '',
    quantity: 1,
    unitPriceDollars: '',
    amountDollars: '0.00',
    kind: null,
  };
}

function initialAuthoringState(): AuthoringDraftState {
  const today = new Date();
  return {
    clientId: null,
    clientName: null,
    clientEmail: null,
    clientPhone: null,
    clientAddress: null,
    lines: [makeBlankLine()],
    taxRateOverride: null,
    paymentTermsDaysOverride: null,
    depositPercentageOverride: null,
    lateFeePolicyOverride: null,
    lateFeeAmountOverride: null,
    issueDate: today.toISOString().slice(0, 10),
    dueDate: addDays(today, 14).toISOString().slice(0, 10),
    notes: '',
    isSalesReceipt: false,
    sourceQuoteId: null,
    clientDefaults: {
      resolved: null,
      sources: null,
      reasoning: null,
      isLoading: false,
      error: null,
    },
  };
}

function hasContent(state: AuthoringDraftState): boolean {
  if (state.clientId !== null) return true;
  if ((state.clientName ?? '').trim().length > 0) return true;
  if (state.notes.trim().length > 0) return true;
  return state.lines.some(
    (l) => l.description.trim().length > 0 || Number(l.amountDollars) > 0,
  );
}

export function MoneyInvoiceAuthoringPanel({ isOpen, onClose, resumeInvoiceId }: Props) {
  const [state, setState] = useState<AuthoringDraftState>(initialAuthoringState);
  const [hydrationDone, setHydrationDone] = useState<boolean>(!resumeInvoiceId);
  const [resumeNumber, setResumeNumber] = useState<string | null>(null);
  const [busy, setBusy] = useState<Action>(null);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const [shapeSheetOpen, setShapeSheetOpen] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const draft = useInvoiceDraft(state, { initialDraftInvoiceId: resumeInvoiceId });

  // Resume hydration — fetch invoice + line_items, populate state.
  useEffect(() => {
    if (!resumeInvoiceId) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('invoices')
        .select(
          'id, invoice_number, client_id, client_name, client_email, client_phone, client_address, line_items, tax_rate, issue_date, due_date, notes, is_sales_receipt, source_quote_id, payment_terms_days_snapshot, deposit_percentage_snapshot, late_fee_policy_snapshot, late_fee_amount_snapshot',
        )
        .eq('id', resumeInvoiceId)
        .maybeSingle();
      if (cancelled || !data) {
        if (!cancelled) {
          setToast({ msg: 'Could not load this draft.', type: 'error' });
          setHydrationDone(true);
        }
        return;
      }
      const rpcLines = (data.line_items ?? []) as unknown as RpcLineItem[];
      const hydratedLines = rpcLines.length > 0 ? linesFromCents(rpcLines) : [makeBlankLine()];
      setResumeNumber(data.invoice_number ?? null);
      // Resume hydration: existing invoice's tax_rate (decimal 0..1)
      // becomes taxRateOverride (UI percent 0..100). Treating it as an
      // override is correct: it was either operator-typed at mint or
      // server-resolved from per-client defaults at that point in time;
      // either way, the saved invoice's rate is the ground truth for
      // resume — we shouldn't re-resolve via the current client_defaults
      // chain since the operator might have intentionally locked a value.
      // NULL existing rate (rare; treated as 0 by Number coercion) →
      // taxRateOverride: null lets server re-resolve on next save.
      const existingDecimal = data.tax_rate === null || data.tax_rate === undefined
        ? null
        : Number(data.tax_rate);
      const taxRateOverride = existingDecimal === null || existingDecimal === 0
        ? null
        : existingDecimal * 100;
      // Phase D v2 — hydrate the 4 override discriminators from the *_snapshot
      // columns Migration 275 captured at mint/update time. Same intent as
      // taxRateOverride: the saved invoice's snapshot is ground truth on
      // resume; operator may have intentionally locked a value, so we don't
      // re-resolve via the current per-client_defaults chain.
      const lateFeePolicySnap = (data as { late_fee_policy_snapshot?: string | null })
        .late_fee_policy_snapshot ?? null;
      setState({
        clientId: data.client_id ?? null,
        clientName: data.client_name ?? null,
        clientEmail: data.client_email ?? null,
        clientPhone: data.client_phone ?? null,
        clientAddress: data.client_address ?? null,
        lines: hydratedLines,
        taxRateOverride,
        paymentTermsDaysOverride:
          (data as { payment_terms_days_snapshot?: number | null })
            .payment_terms_days_snapshot ?? null,
        depositPercentageOverride:
          (data as { deposit_percentage_snapshot?: number | string | null })
            .deposit_percentage_snapshot != null
            ? Number(
                (data as { deposit_percentage_snapshot: number | string })
                  .deposit_percentage_snapshot,
              )
            : null,
        lateFeePolicyOverride:
          lateFeePolicySnap === 'percent' || lateFeePolicySnap === 'flat'
            ? lateFeePolicySnap
            : null,
        lateFeeAmountOverride:
          (data as { late_fee_amount_snapshot?: number | string | null })
            .late_fee_amount_snapshot != null
            ? Number(
                (data as { late_fee_amount_snapshot: number | string })
                  .late_fee_amount_snapshot,
              )
            : null,
        issueDate: data.issue_date ?? new Date().toISOString().slice(0, 10),
        dueDate: data.due_date ?? null,
        notes: data.notes ?? '',
        isSalesReceipt: data.is_sales_receipt ?? false,
        sourceQuoteId: data.source_quote_id ?? null,
        clientDefaults: {
          resolved: null,
          sources: null,
          reasoning: null,
          isLoading: false,
          error: null,
        },
      });
      setHydrationDone(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeInvoiceId]);

  const onPatch = useCallback((patch: Partial<AuthoringDraftState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Phase 5 Migration B sub-track (c) — "Start from a saved shape" applies a
  // shape blob (line_items + notes + payment_terms_days + tax_type) to draft.
  const onApplyShape = useCallback((blob: InvoiceShapeApplyBlob) => {
    setState((prev) => applyShapeBlobToDraft(prev, blob));
    setShapeSheetOpen(false);
    setToast({ msg: `Started from "${blob.name}".`, type: 'success' });
  }, []);
  const onShapeError = useCallback(
    (message: string) => setToast({ msg: message, type: 'error' }),
    [],
  );

  const resetAndClose = useCallback(() => {
    onClose();
    // Reset local form state so the next mount opens fresh. Hook idempotency
    // key resets via panel-unmount when the parent's `isOpen` gates rendering.
    setState(initialAuthoringState());
    setHydrationDone(true);
    setResumeNumber(null);
    setBusy(null);
    setUnsavedOpen(false);
  }, [onClose]);

  // Helper: ensure a draft exists, returning its id or null on failure.
  // Uses the resolved id from save() directly (Session 127 hook patch) — no
  // closure-capture race on first create.
  const ensureDraft = async (
    override?: Partial<AuthoringDraftState>,
  ): Promise<string | null> => {
    const result = await draft.save(override);
    if (!result.success) {
      const code = (result.error as MoneyErrorCode | undefined) ?? 'UNKNOWN';
      setToast({ msg: lookupErrorCopy('create_invoice', code), type: 'error' });
      return null;
    }
    return result.invoiceId ?? null;
  };

  const onSendNow = async () => {
    if (busy) return;
    setBusy('send');
    try {
      const id = await ensureDraft();
      if (!id) return;
      const result = await markSent(id, 'manual');
      if (!result.success) {
        setToast({
          msg: lookupErrorCopy('mark_invoice_sent', result.error.code),
          type: 'error',
        });
        return;
      }
      setToast({ msg: 'Invoice sent.', type: 'success' });
      resetAndClose();
    } finally {
      setBusy(null);
    }
  };

  const onSaveDraft = async () => {
    if (busy) return;
    setBusy('draft');
    try {
      const id = await ensureDraft();
      if (!id) return;
      setToast({ msg: 'Draft saved.', type: 'success' });
      resetAndClose();
    } finally {
      setBusy(null);
    }
  };

  const onSendAndCash = async () => {
    if (busy) return;
    setBusy('cash');
    try {
      // §3f — walk-in cash flips is_sales_receipt to true. Pass the override
      // directly into save() so the immediate write picks it up (stateRef
      // doesn't refresh until the next render+effect cycle); also patch local
      // state so subsequent renders show the flipped flag.
      const isWalkIn = state.clientId === null;
      const flagOverride = isWalkIn !== state.isSalesReceipt
        ? { isSalesReceipt: isWalkIn }
        : undefined;
      if (flagOverride) {
        setState((prev) => ({ ...prev, isSalesReceipt: isWalkIn }));
      }
      const id = await ensureDraft(flagOverride);
      if (!id) return;

      const subtotal = subtotalDollars(state.lines);
      const tax = taxDollars(subtotal, resolveTaxRatePercent(state));
      const total = addMoney(subtotal, tax);
      const numericTotal = Number(total);

      const sentResult = await markSent(id, 'manual');
      if (!sentResult.success) {
        setToast({
          msg: lookupErrorCopy('mark_invoice_sent', sentResult.error.code),
          type: 'error',
        });
        return;
      }
      const paidResult = await markPaidManually(id, {
        amount: numericTotal,
        method: 'cash',
        receivedAt: new Date().toISOString(),
      });
      if (!paidResult.success) {
        setToast({
          msg: lookupErrorCopy('set_invoice_paid_manually', paidResult.error.code),
          type: 'error',
        });
        return;
      }
      setToast({ msg: 'Cash payment recorded.', type: 'success' });
      resetAndClose();
    } finally {
      setBusy(null);
    }
  };

  const onCloseAttempt = () => {
    if (busy) return;
    if (hasContent(state) && hydrationDone) {
      setUnsavedOpen(true);
    } else {
      resetAndClose();
    }
  };

  const onUnsavedSave = async () => {
    setUnsavedOpen(false);
    await onSaveDraft();
  };
  const onUnsavedDiscard = async () => {
    setUnsavedOpen(false);
    if (draft.draftInvoiceId) {
      const result = await deleteDraftInvoice(draft.draftInvoiceId);
      if (!result.success) {
        setToast({
          msg: lookupErrorCopy('delete_draft_invoice', result.error.code as MoneyErrorCode),
          type: 'error',
        });
        return;
      }
      setToast({ msg: 'Draft discarded.', type: 'success' });
    }
    resetAndClose();
  };
  const onUnsavedCancel = () => setUnsavedOpen(false);

  // Validation
  const trimmedName = (state.clientName ?? '').trim();
  const isWalkIn = state.clientId === null && trimmedName.length > 0;
  const hasClient = state.clientId !== null || isWalkIn;
  const hasValidLine = state.lines.some(
    (l) => l.description.trim().length > 0 && Number(l.amountDollars) > 0,
  );
  const validationDisabled = !hasClient || !hasValidLine;

  const subtotal = subtotalDollars(state.lines);
  const tax = taxDollars(subtotal, resolveTaxRatePercent(state));
  const total = addMoney(subtotal, tax);
  const cashAvailable = Number(total) > 0;

  const subtitle = draft.draftInvoiceNumber ?? resumeNumber ?? undefined;
  const title = resumeInvoiceId ? 'Edit draft' : 'New invoice';

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={onCloseAttempt}
        title={title}
        subtitle={subtitle}
        width={720}
      >
        {/* Gate heavy form content on isOpen so DatePicker / inputs don't
            mount until the panel is reachable — keeps page-load lean and
            avoids spurious hydration warnings from primitives rendered
            off-screen. */}
        {isOpen && (!hydrationDone ? (
          <SkeletonBody />
        ) : (
          <>
            {!resumeInvoiceId && state.clientId === null && state.sourceQuoteId === null && (
              <StartFromShapeEntryButton onClick={() => setShapeSheetOpen(true)} />
            )}
            <AuthoringClientSection state={state} onChange={onPatch} />
            <AuthoringLineItemsSection state={state} onChange={onPatch} />
            <AuthoringReviewSection state={state} onChange={onPatch} />
            <AuthoringActionFooter
              isAutoSaving={draft.isAutoSaving}
              lastSavedAt={draft.lastSavedAt}
              error={draft.error}
              onSendNow={() => void onSendNow()}
              onSaveDraft={() => void onSaveDraft()}
              onSendAndCash={() => void onSendAndCash()}
              onRetrySave={() => void draft.save()}
              busy={busy}
              validationDisabled={validationDisabled}
              cashAvailable={cashAvailable}
            />
          </>
        ))}
      </SlidePanel>

      <UnsavedChangesDialog
        isOpen={unsavedOpen}
        onSaveDraft={() => void onUnsavedSave()}
        onDiscard={() => void onUnsavedDiscard()}
        onCancel={onUnsavedCancel}
        saving={busy === 'draft'}
      />

      <StartFromShapeSheet
        isOpen={shapeSheetOpen}
        onClose={() => setShapeSheetOpen(false)}
        onApplied={onApplyShape}
        onError={onShapeError}
      />

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          isVisible={!!toast}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

function SkeletonBody() {
  return (
    <div>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: i === 1 ? 80 : 180,
            background: palette.g100,
            borderRadius: 12,
            marginBottom: spacing.md,
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  );
}
